import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, REDRAW, EVIDENCE, WORSE, FIX, HOW, LESSON, OUTRO } from "./script";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 24,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 90,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 1.02,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1620,
    }}
  >
    {children}
  </div>
);

const Mono: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 22,
}) => (
  <div style={{ fontFamily: FONTS.mono, fontSize: size, lineHeight: 1.7 }}>
    {children}
  </div>
);

const Chip: React.FC<{ children: React.ReactNode; color: string }> = ({
  children,
  color,
}) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      color,
      border: `1px solid ${color}66`,
      borderRadius: 8,
      padding: "10px 18px",
    }}
  >
    {children}
  </div>
);

/* a hard-bordered diagram box (the "authoritative"-looking node) */
const DiagramBox: React.FC<{ label: string; color?: string; dim?: boolean }> = ({
  label,
  color = COLORS.cyan,
  dim = false,
}) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 21,
      color: dim ? COLORS.dim : COLORS.text,
      border: `1.5px solid ${dim ? COLORS.line : color}`,
      borderRadius: 8,
      padding: "12px 18px",
      background: COLORS.panel,
      boxShadow: dim ? "none" : `0 0 22px -14px ${color}`,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </div>
);

const Arrow: React.FC<{ color?: string }> = ({ color = COLORS.dim }) => (
  <div style={{ color, fontSize: 26 }}>→</div>
);

/* the clean, confident (but wrong) architecture the AI drew */
const MiniArch: React.FC = () => (
  <Panel accent="cyan" title="architecture · action-oriented dynamic ui" width={880}>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
        padding: "8px 0 4px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <DiagramBox label="Client" />
        <Arrow />
        <DiagramBox label="Gateway" />
        <Arrow />
        <DiagramBox label="Engine" />
      </div>
      <div style={{ color: COLORS.dim, fontSize: 24 }}>↓</div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <DiagramBox label="Store" />
        <Arrow />
        <DiagramBox label="Renderer" />
        <Arrow />
        <DiagramBox label="Surface" />
      </div>
    </div>
  </Panel>
);

/* reusable before(red) → after(green) pair that reveals in sequence */
const BeforeAfter: React.FC<{
  beforeTitle: string;
  before: React.ReactNode;
  afterTitle: string;
  after: React.ReactNode;
  panelWidth?: number;
}> = ({ beforeTitle, before, afterTitle, after, panelWidth = 540 }) => {
  const frame = useCurrentFrame();
  const reveal = (f: number) => interpolate(frame, [f, f + 18], [0, 1], clamp);
  return (
    <div style={{ display: "flex", alignItems: "stretch", gap: 30 }}>
      <div style={{ opacity: reveal(10) }}>
        <Panel accent="red" title={beforeTitle} width={panelWidth}>
          {before}
        </Panel>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          fontFamily: FONTS.display,
          fontWeight: 700,
          fontSize: 52,
          color: COLORS.dim,
          opacity: reveal(44),
        }}
      >
        →
      </div>
      <div style={{ opacity: reveal(58) }}>
        <Panel accent="green" title={afterTitle} width={panelWidth}>
          {after}
        </Panel>
      </div>
    </div>
  );
};

/* 01 — HOOK : a clean, confident diagram · PASSED · then the WRONG stamp lands */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const passed = interpolate(frame, [46, 74], [0, 1], clamp);
  const wrongOp = interpolate(frame, [150, 168], [0, 1], clamp);
  const wrongScale = interpolate(frame, [150, 178], [1.55, 1], clamp);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="i asked for the architecture diagram" color={COLORS.cyan} />
          <div style={{ position: "relative" }}>
            <MiniArch />
            <div
              style={{
                position: "absolute",
                top: -18,
                right: -22,
                opacity: passed,
                fontFamily: FONTS.mono,
                fontSize: 22,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: COLORS.green,
                border: `2px solid ${COLORS.green}`,
                borderRadius: 8,
                padding: "8px 16px",
                background: "rgba(11,13,18,0.9)",
                transform: "rotate(-4deg)",
                boxShadow: `0 0 24px -8px ${COLORS.green}`,
              }}
            >
              coverage: passed ✓
            </div>
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: wrongOp,
                transform: `rotate(-11deg) scale(${wrongScale})`,
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.display,
                  fontWeight: 700,
                  fontSize: 128,
                  letterSpacing: "0.04em",
                  color: COLORS.red,
                  border: `5px solid ${COLORS.red}`,
                  borderRadius: 14,
                  padding: "4px 34px",
                  background: "rgba(11,13,18,0.35)",
                  textShadow: `0 0 44px ${COLORS.red}66`,
                }}
              >
                WRONG
              </span>
            </div>
          </div>
          <div
            style={{
              marginTop: 40,
              fontFamily: FONTS.mono,
              fontSize: 25,
              color: COLORS.text,
            }}
          >
            looked authoritative · passed the grader ·{" "}
            <span style={{ color: COLORS.red }}>wrong diagram</span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="red" />
    </AbsoluteFill>
  );
};

/* 02 — REDRAW : doc → text chunks → LLM draws a NEW diagram · the real one sits unused */
export const Scene02Redraw: React.FC = () => {
  const frame = useCurrentFrame();
  const greyed = interpolate(frame, [150, 190], [0, 0.45], clamp);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.redraw.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="redraw, not reproduce" color={COLORS.magenta} />
          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            <DiagramBox label="architecture.html" />
            <Arrow />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  width: 190,
                  justifyContent: "center",
                }}
              >
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: 52,
                      height: 18,
                      borderRadius: 4,
                      background: COLORS.panel2,
                      border: `1px solid ${COLORS.cyan}55`,
                    }}
                  />
                ))}
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 16, color: COLORS.dim }}>
                text chunks
              </div>
            </div>
            <Arrow color={COLORS.magenta} />
            <DiagramBox label="LLM" color={COLORS.magenta} />
            <Arrow color={COLORS.magenta} />
            <DiagramBox label="a NEW diagram" color={COLORS.magenta} />
          </div>
          <div
            style={{
              marginTop: 40,
              display: "flex",
              alignItems: "center",
              gap: 18,
              opacity: greyed,
            }}
          >
            <DiagramBox label="the real diagram" dim />
            <span style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim }}>
              sits right there · never copied · greyed out, unused
            </span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={REDRAW} accent="magenta" />
    </AbsoluteFill>
  );
};

/* 03 — EVIDENCE : the real architecture vs the mashup it drew · grader can't see faithfulness */
export const Scene03Evidence: React.FC = () => {
  const frame = useCurrentFrame();
  const grader = interpolate(frame, [96, 128], [0, 1], clamp);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.evidence.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the evidence · present ≠ faithful" color={COLORS.amber} />
          <div style={{ display: "flex", alignItems: "stretch", gap: 30 }}>
            <Panel accent="green" title="the real architecture" width={560}>
              <Mono>
                <div style={{ color: COLORS.green }}>descriptor contract</div>
                <div style={{ color: COLORS.dim, fontSize: 18 }}>↓ one schema</div>
                <div style={{ color: COLORS.green }}>three surfaces</div>
                <div style={{ color: COLORS.dim, fontSize: 18 }}>chat · mission · dashboard</div>
                <div style={{ color: COLORS.green }}>one action spine</div>
              </Mono>
            </Panel>
            <Panel accent="red" title="what the ai drew" width={560}>
              <Mono>
                <div style={{ color: COLORS.text }}>mission-step dispatch flow</div>
                <div style={{ color: COLORS.red }}>⊕ draft-approval widget ← merged</div>
                <div style={{ color: COLORS.dim, fontSize: 18, marginTop: 8 }}>dropped:</div>
                <div style={{ color: COLORS.red, textDecoration: "line-through" }}>
                  descriptor contract
                </div>
                <div style={{ color: COLORS.red, textDecoration: "line-through" }}>
                  three surfaces · action spine
                </div>
              </Mono>
            </Panel>
          </div>
          <div style={{ display: "flex", gap: 18, marginTop: 30, opacity: grader }}>
            <Chip color={COLORS.green}>✓ diagram present</Chip>
            <Chip color={COLORS.amber}>faithful? ❓</Chip>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={EVIDENCE} accent="red" />
    </AbsoluteFill>
  );
};

/* 04 — WORSE : the diagram-bearing HTML doc embedded poorly · wasn't even retrieved */
const WORSE_NODES: PipeNode[] = [
  { label: "embedding", value: "weak vector", sub: "markup dilutes it", accent: "amber" },
  { label: "ranking", value: "ranks low", sub: "below the cut", accent: "amber" },
  { label: "retrieval", value: "not retrieved", sub: "never seen by the model", accent: "red" },
];
export const Scene04Worse: React.FC = () => {
  const frame = useCurrentFrame();
  const invented = interpolate(frame, [120, 150], [0, 1], clamp);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.worse.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="and it gets worse" color={COLORS.amber} />
          <Panel accent="amber" title="architecture.html · a visual companion" width={780}>
            <pre
              style={{
                fontFamily: FONTS.mono,
                fontSize: 19,
                lineHeight: 1.55,
                margin: 0,
                color: COLORS.dim,
                whiteSpace: "pre",
              }}
            >
              {`<section class="architecture">
  <h2>Action-Oriented Dynamic UI</h2>
  `}
              <span style={{ color: COLORS.amber }}>{`<div class="mermaid">`}</span>
              {`
    graph TD
      descriptor --> surfaces
      surfaces --> spine
  `}
              <span style={{ color: COLORS.amber }}>{`</div>`}</span>
              {`
</section>`}
            </pre>
          </Panel>
          <div style={{ color: COLORS.dim, fontSize: 24, margin: "12px 0" }}>↓</div>
          <div style={{ width: 1180 }}>
            <PipelineGraph nodes={WORSE_NODES} startFrame={30} stepFrames={20} nodeWidth={300} />
          </div>
          <div
            style={{
              opacity: invented,
              marginTop: 26,
              fontFamily: FONTS.mono,
              fontSize: 23,
              color: COLORS.text,
              border: `1px solid ${COLORS.red}55`,
              borderRadius: 8,
              padding: "10px 20px",
            }}
          >
            the doc that HAD the answer wasn't retrieved →{" "}
            <span style={{ color: COLORS.red }}>so the model invented one</span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={WORSE} accent="amber" />
    </AbsoluteFill>
  );
};

/* 05 — FIX : source-diagram passthrough · reproduce from source, don't redraw from memory */
export const Scene05Fix: React.FC = () => {
  const frame = useCurrentFrame();
  const honest = interpolate(frame, [150, 185], [0, 1], clamp);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.fix.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the fix · source-diagram passthrough" color={COLORS.green} />
          <div
            style={{
              fontFamily: FONTS.display,
              fontWeight: 700,
              fontSize: 50,
              letterSpacing: "-0.02em",
              color: COLORS.text,
              marginBottom: 26,
              textAlign: "center",
            }}
          >
            reproduce the blueprint — don't redraw it
          </div>
          <BeforeAfter
            panelWidth={560}
            beforeTitle="redraw from memory"
            before={
              <Mono>
                <span style={{ color: COLORS.dim, textDecoration: "line-through" }}>
                  paraphrase the blueprint
                </span>
                <br />
                <span style={{ color: COLORS.red }}>from prose · from memory</span>
                <br />
                <span style={{ color: COLORS.dim }}>omits · conflates · invents</span>
              </Mono>
            }
            afterTitle="reproduce from source"
            after={
              <Mono>
                <span style={{ color: COLORS.green }}>find the real diagram in a source</span>
                <br />
                <span style={{ color: COLORS.text }}>inject it VERBATIM</span>
                <br />
                <span style={{ color: COLORS.dim }}>reproduce-exactly directive</span>
                <br />
                <span style={{ color: COLORS.dim }}>synthesis = labeled fallback</span>
              </Mono>
            }
          />
          <div
            style={{
              opacity: honest,
              marginTop: 30,
              maxWidth: 1240,
              textAlign: "center",
              fontFamily: FONTS.mono,
              fontSize: 21,
              color: COLORS.amber,
              border: `1px solid ${COLORS.amber}55`,
              borderRadius: 8,
              padding: "12px 22px",
            }}
          >
            honest: today the passthrough directive strongly <em>nudges</em> the model to reproduce
            verbatim — a fully-deterministic server-side render is the next slice.
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={FIX} accent="green" />
    </AbsoluteFill>
  );
};

/* 06 — HOW : detect → extract → match → rescue → inject verbatim */
const HOW_NODES: PipeNode[] = [
  { label: "step 1", value: "detect", sub: "a diagram request", accent: "cyan" },
  { label: "step 2", value: "extract", sub: "```mermaid + <div>", accent: "green" },
  { label: "step 3", value: "match", sub: "query-term overlap", accent: "lime" },
  { label: "step 4", value: "rescue", sub: "bounded DB search", accent: "amber" },
  { label: "step 5", value: "inject", sub: "verbatim", accent: "magenta" },
];
export const Scene06How: React.FC = () => {
  const frame = useCurrentFrame();
  const tag = interpolate(frame, [140, 172], [0, 1], clamp);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.how.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
          <Kicker text="how it works · five steps" color={COLORS.cyan} />
          <div style={{ width: 1740, marginTop: 8 }}>
            <PipelineGraph nodes={HOW_NODES} startFrame={8} stepFrames={22} nodeWidth={300} />
          </div>
          <div
            style={{
              opacity: tag,
              marginTop: 46,
              fontFamily: FONTS.mono,
              fontSize: 24,
              color: COLORS.text,
              border: `1px solid ${COLORS.line}`,
              borderRadius: 8,
              padding: "12px 22px",
            }}
          >
            domain-agnostic · fail-safe ·{" "}
            <span style={{ color: COLORS.cyan }}>shows up in the trace</span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOW} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 07 — LESSON : reproduce ground truth · synthesis is the fallback */
export const Scene07Lesson: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.lesson.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
        <Kicker text="the rule" color={COLORS.cyan} />
        <Big size={64}>
          <span style={{ color: COLORS.green }}>Reproduce ground truth.</span>
          <br />
          Synthesis is the <span style={{ color: COLORS.amber }}>fallback.</span>
        </Big>
        <div
          style={{
            marginTop: 44,
            fontFamily: FONTS.mono,
            fontSize: 26,
            color: COLORS.dim,
            textAlign: "center",
          }}
        >
          an answer-shaped thing is not a{" "}
          <span style={{ color: COLORS.text }}>faithful</span> answer.
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={LESSON} accent="green" />
  </AbsoluteFill>
);

/* 08 — OUTRO : build-in-public closer */
export const Scene08Outro: React.FC = () => {
  const frame = useCurrentFrame();
  const glow = 0.6 + 0.4 * Math.sin(frame / 16);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Big size={58}>
            A generated diagram looks{" "}
            <span style={{ color: COLORS.cyan }}>authoritative.</span>
            <br />
            That's exactly why you can't trust it
            <br />
            until it <span style={{ color: COLORS.green }}>reproduces the real one.</span>
          </Big>
          <div
            style={{
              marginTop: 40,
              fontFamily: FONTS.mono,
              fontSize: 24,
              color: COLORS.text,
              textShadow: `0 0 30px rgba(34,224,255,${0.22 * glow})`,
            }}
          >
            it's all open · the repo · the extractor · the fix
          </div>
          <div
            style={{
              marginTop: 18,
              fontFamily: FONTS.mono,
              fontSize: 22,
              letterSpacing: "0.24em",
              color: COLORS.dim,
            }}
          >
            EAKC · building in public
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={OUTRO} accent="cyan" />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
